import {
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import TableContainer from "../../../../Components/TableContainer/TableContainer";
import { useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import useDebounce from "../../../../Components/useDebounce/useDebounce";
import moment from "moment";
import BarChartIcon from "@mui/icons-material/BarChart";
import MonthlyCandelChartModel from "./MonthlyCendelChartModel";
import FundsPageShell from "../../../../Components/FundsPeriodTotals/FundsPageShell";
import FundsPageToolbar from "../../../../Components/FundsPeriodTotals/FundsPageToolbar";
import useMonthlyGraphPeriodTotals from "../../../../hooks/useMonthlyGraphPeriodTotals";
import {
  filterByClientSegment,
  filterByMonthlyGraphPeriod,
  getDefaultFundsPeriodEnd,
  getDefaultFundsPeriodStart,
  hasActiveFundsFilters,
} from "../../../../Utils/fundsPeriodTotals";
import { dolorFormatAmount } from "../../../../Utils/Services";
import DOCUMENT_ICON from "../../../../Assets/Images/Common/documentation.png";
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import Logo from '../../../../Assets/Images/Common/pdf_logo.png';
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
const MonthlyGraph = () => {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [monthlyGraphList, setMonthlyGraphList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [performanceShowModel, setPerformanceShowModel] = useState(false);
  const [performanceGraphDetails, setPerformanceGraphDetails] = useState({});
  const [searchCustmer, setSearchCustmer] = useState("");
  const debouncedSearch = useDebounce(searchCustmer, 500);
  const [usersMap, setUsersMap] = useState({});
  const [clientSegment, setClientSegment] = useState(null);
  const [periodStartDate, setPeriodStartDate] = useState(getDefaultFundsPeriodStart);
  const [periodEndDate, setPeriodEndDate] = useState(getDefaultFundsPeriodEnd);
  const [dateFilterActive, setDateFilterActive] = useState(false);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);
  const { totals: periodTotals, loading: periodTotalsLoading } = useMonthlyGraphPeriodTotals({
    startDate: periodStartDate,
    endDate: periodEndDate,
    usersMap,
  });

  const filtersActive = hasActiveFundsFilters({
    search: searchCustmer,
    status: null,
    clientSegment,
    dateFilterActive,
    periodStartDate,
    periodEndDate,
  });

  const resetAllFilters = () => {
    setSearchCustmer("");
    setClientSegment(null);
    setDateFilterActive(false);
    setPeriodStartDate(getDefaultFundsPeriodStart());
    setPeriodEndDate(getDefaultFundsPeriodEnd());
    setDatePickerResetKey((key) => key + 1);
    setPage(0);
  };

  const handleClientSegmentChange = (segment) => {
    setClientSegment(segment);
    setPage(0);
  };

  const handlePeriodStartChange = (value) => {
    if (!value) return;
    setDateFilterActive(true);
    setPeriodStartDate(value);
    setPage(0);
    if (periodEndDate && value.isAfter(periodEndDate)) {
      setPeriodEndDate(value);
    }
  };

  const handlePeriodEndChange = (value) => {
    if (!value) return;
    setDateFilterActive(true);
    setPeriodEndDate(value);
    setPage(0);
    if (periodStartDate && value.isBefore(periodStartDate)) {
      setPeriodStartDate(value);
    }
  };

  useEffect(() => {
    getDocs(collection(DATABASE, "users"))
      .then((snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          map[d.id] = { doc_id: d.id, ...d.data() };
        });
        setUsersMap(map);
      })
      .catch((err) => console.error("MonthlyGraph: failed to load users", err));
  }, []);

  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (ts?.toDate && typeof ts.toDate === "function") return moment(ts.toDate());
    const seconds = ts?.seconds;
    const nanoseconds = ts?.nanoseconds;
    if (seconds == null) return null;
    const milliseconds = seconds * 1000 + Math.floor((nanoseconds || 0) / 1000000);
    return moment(milliseconds);
  };

  const columns = [
    {
      field: "rowid",
      headerName: "No.",
      width: 60,
      minWidth: 60,
      hideOnMobile: true,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "14px", fontWeight: "400" }}>
          {params?.row?.rowid}
        </Typography>
      ),
    },
    {
      field: "user_name",
      headerName: "Name",
      minWidth: 200,
      cellClassName: "leftAlign",
      renderCell: (params) => (
        <Tooltip title={params?.row?.user_name || "--"}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%", display: "block" }}>
            {params?.row?.user_name || "--"}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      hideOnMobile: true,
      renderCell: (params) => (
        <Tooltip title={params?.row?.email || "--"}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%", display: "block" }}>
            {params?.row?.email || "--"}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "start_date",
      headerName: "Month Date",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const month = params?.row?.graph_month;
        const year = params?.row?.graph_year;
        if (year != null && month != null && month >= 0 && month <= 11) {
          return <div>{moment({ year, month, day: 1 }).format("MMMM YYYY")}</div>;
        }
        const dateP = parseTimestamp(params?.row?.start_date);
        return <div>{dateP ? dateP.format("MMMM YYYY") : "--"}</div>;
      },
    },
    {
      field: "total_Monthly_percantage",
      headerName: "Fund Perfor.",
      minWidth: 120,
      hideOnMobile: true,
      renderCell: (params) => {
        const fundPerfor = parseFloat(params?.row?.total_Monthly_percantage) || 0;
        const indPerfor = parseFloat(params?.row?.gross_performance_percentage) || 0;
        const differs = Math.abs(fundPerfor - indPerfor) > 0.01;
        const data = `${fundPerfor.toFixed(2)}%`;
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...(differs && { backgroundColor: "rgba(255, 193, 7, 0.12)", borderRadius: "4px" }),
            }}
          >
            {data}
          </Box>
        );
      },
    },
    {
      field: "fiddux_commission",
      headerName: "Fiddux Commission",
      minWidth: 140,
      hideOnMobile: true,
      renderCell: (params) => {
        const data = params?.row?.fiddux_commission;
        return <>{data ? "$" + dolorFormatAmount(data) : "-"}</>;
      },
    },
    {
      field: "profit_to_user",
      headerName: "Client Profit",
      minWidth: 120,
      hideOnMobile: true,
      renderCell: (params) => {
        const data = params?.row?.profit_to_user;
        return <>{data ? "$" + dolorFormatAmount(data) : "-"}</>;
      },
    },
    {
      field: "gross_performance_percentage",
      headerName: "Ind. Perfor.",
      minWidth: 110,
      renderCell: (params) => {
        const indPerfor = parseFloat(params?.row?.gross_performance_percentage) || 0;
        const fundPerfor = parseFloat(params?.row?.total_Monthly_percantage) || 0;
        const differs = Math.abs(indPerfor - fundPerfor) > 0.01;
        const data = `${indPerfor.toFixed(2)}%`;
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...(differs && { backgroundColor: "rgba(255, 193, 7, 0.12)", borderRadius: "4px" }),
            }}
          >
            {data}
          </Box>
        );
      },
    },
    {
      field: "isNotify",
      headerName: "PDF Documents",
      width: 110,
      minWidth: 110,
      renderCell: (params) => (
        <IconButton onClick={() => handleOpenPDFDocument(params?.row)}>
          <img src={DOCUMENT_ICON} alt="pdfFile" style={{ width: "50px" }} />
        </IconButton>
      ),
    },
    {
      field: "graph_points",
      headerName: "Graph",
      width: 70,
      minWidth: 70,
      renderCell: (params) => (
        <Typography
          sx={{ fontSize: "14px", fontWeight: "400", cursor: "pointer" }}
          onClick={() => handleShowHideMonthlyGraph(params?.row)}
        >
          <BarChartIcon />
        </Typography>
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      width: 90,
      minWidth: 90,
      filterable: false,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { isNotify } = params?.row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            <Tooltip title="Send PDF" key={`send-pdf-${params.row.id || params.row.doc_id || params.id}`}>
              <IconButton
                sx={{
                  background: "#4caf50",
                  width: "30px",
                  height: "30px",
                  borderRadius: "4px",
                  "&:hover": { background: "#388e3c" },
                }}
                onClick={() => handleSendPDFMail(params?.row)}
              >
                <CheckIcon sx={{ color: "#fff" }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];
  useEffect(() => {
    const unsubscribe = getMonthlyList(
      page,
      pageSize,
      debouncedSearch,
      dateFilterActive ? periodStartDate : null,
      dateFilterActive ? periodEndDate : null,
      clientSegment
    );
    return () => unsubscribe?.();
  }, [page, pageSize, debouncedSearch, periodStartDate, periodEndDate, dateFilterActive, clientSegment, usersMap]);

  const getMonthlyList = (
    currentPage = 0,
    pageSize = 50,
    debouncedSearch = "",
    periodStart = periodStartDate,
    periodEnd = periodEndDate,
    segmentFilter = clientSegment
  ) => {
    setIsLoading(true);

    let q = query(
      collection(DATABASE, "monthly_grpahs"),
      orderBy("graph_month_date", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        const transactions = querySnapshot?.docs?.map((doc, index) => ({
          rowid: index + 1,
          id: doc?.id,
          ...doc?.data(),
        }));

        const monthlyWithUserNames = transactions?.map((elem) => {
          const matchedUser = usersMap[elem?.user_id];
          return {
            ...elem,
            user_name: matchedUser?.full_name || matchedUser?.name || "Unknown",
            email: matchedUser?.email || "--",
            registered_date: matchedUser?.registered_date || "--",
            user_type: matchedUser?.user_type,
          };
        });

        const periodFiltered = filterByMonthlyGraphPeriod(
          monthlyWithUserNames,
          periodStart,
          periodEnd
        );
        const segmentFiltered = filterByClientSegment(periodFiltered, segmentFilter, usersMap);
        const filteredMonthly = debouncedSearch
          ? segmentFiltered?.filter(
            (elem) =>
              elem?.email
                .toLowerCase()
                .includes(debouncedSearch.toLowerCase()) ||
              elem?.user_name
                .toLowerCase()
                .includes(debouncedSearch.toLowerCase())
          )
          : segmentFiltered;

        const paginatedMonthly = filteredMonthly?.slice(
          currentPage * pageSize,
          (currentPage + 1) * pageSize
        );

        setMonthlyGraphList(paginatedMonthly);
        setTotalCount(filteredMonthly?.length);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching deposits:", error);
        setIsLoading(false);
      }
    );
    return unsubscribe;
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    getMonthlyList(0, newPageSize, debouncedSearch);
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
    getMonthlyList(newPage, pageSize, debouncedSearch);
  };
  const handleShowHideMonthlyGraph = (graphPoints) => {
    setPerformanceShowModel(true);
    setPerformanceGraphDetails(graphPoints);
  };
  const handleOpenPDFDocument = async (graphPoints) => {

    const userId = graphPoints?.user_id;
    const reportMonth = moment(graphPoints?.graph_month_date.toDate());

    try {
      const userDoc = await getDoc(doc(DATABASE, "users", userId));
      if (!userDoc.exists) {
        console.log(`No user data found for ID: ${userId}`);
        return;
      }
      const user = userDoc?.data();
      if (!user) {
        console.log(`Invalid user data for ID: ${userId}`);
        return;
      }

      const grossPerformancePercentage = graphPoints?.gross_performance_percentage || 0;
      const percentageAfterCommission = dolorFormatAmount(graphPoints?.profit_to_user || 0);

      const updatedBalance = Number(user?.net_balance);
      const formattedUpdatedBalance = dolorFormatAmount(updatedBalance);

      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([600, 700]);
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const logoBytes = await fetch(Logo).then((res) => res.arrayBuffer());
      const logoImage = await pdfDoc.embedPng(logoBytes);
      const logoWidth = 200;
      const logoHeight = 47;

      const title = "FIDUSFINANCE OÜ";
      const subtitle = `Monthly Report – ${reportMonth?.format("MMMM YYYY")}`;
      const clientName = `Mr. ${user?.full_name}`;
      const reportContent = `In alignment with our dedication to market analysis and transparency, FIDDUX is pleased to present its monthly performance report, which is based on extensive technical analysis and strategic positioning within the digital assets markets. Our committed team diligently continues to monitor and assess market trends to provide our clients with the best results and outcomes.`;
      const startDate = reportMonth.startOf('month').format("DD/MM/YYYY");
      const endDate = reportMonth.endOf('month').format("DD/MM/YYYY");

      const overallPerformance = `Overall performance: ${grossPerformancePercentage?.toFixed(2)}%`;
      const accountPerformance = `Account performance: ${percentageAfterCommission} USDT`;
      const updatedBalanceText = `Updated account balance: ${formattedUpdatedBalance} USDT`;

      const address = "FidusFinance OÜ - 16290879";
      const city = "Jõe tn 2a Tallinn Harjumaa 10151";
      const email = "order@fiddux.com";

      page.drawImage(logoImage, { x: 200, y: 600, width: logoWidth, height: logoHeight });
      page.drawText(title, { x: 50, y: 520, size: 16, font, color: rgb(0, 0, 0) });
      page.drawText(subtitle, { x: 50, y: 500, size: 14, font, color: rgb(0, 0, 0) });
      page.drawText(clientName, { x: 50, y: 480, size: 12, font, color: rgb(0, 0, 0) });
      page.drawText(reportContent, { x: 50, y: 420, size: 12, font, color: rgb(0, 0, 0), maxWidth: 500, lineHeight: 21 });
      page.drawText(`Start date: ${startDate}`, { x: 50, y: 300, size: 12, font, color: rgb(0, 0, 0) });
      page.drawText(`End date: ${endDate}`, { x: 50, y: 280, size: 12, font, color: rgb(0, 0, 0) });
      page.drawText(overallPerformance, { x: 50, y: 260, size: 12, font, color: rgb(0, 0, 0) });
      page.drawText(accountPerformance, { x: 50, y: 240, size: 12, font, color: rgb(0, 0, 0) });
      page.drawText(updatedBalanceText, { x: 50, y: 220, size: 12, font, color: rgb(0, 0, 0) });
      const drawCenteredText = (text, yPosition, fontSize) => {
        const textWidth = font.widthOfTextAtSize(text, fontSize);
        const pageWidth = page.getWidth();
        const xPosition = (pageWidth - textWidth) / 2;
        page.drawText(text, {
          x: xPosition,
          y: yPosition,
          size: fontSize,
          font: font,
          color: rgb(0, 0, 0),
        });
      };
      drawCenteredText(address, 110, 12);
      drawCenteredText(city, 90, 12);
      drawCenteredText(email, 70, 12);
      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };
  const handleSendPDFMail = async (graphPoints) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `would you like to send the report to ${graphPoints?.user_name}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#46adf2",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        const FUNCTIONS_URLS = (await import('../../../../Config/functions')).default;
        const selectedMonth = moment(graphPoints?.graph_month_date.toDate());
        const matchingEntries = []
        matchingEntries.push(graphPoints);
        const isNotify = [];
        isNotify.push(graphPoints?.user_id);
        const response = await axios.post(
          FUNCTIONS_URLS.SendPdfMonthlyPerformance,
          { matchingEntries, isNotify, selectedMonth: selectedMonth.format('YYYY-MM') },
          { 
            headers: { 'Content-Type': 'application/json' },
            timeout: 300000 // 5 minute timeout for PDF generation (matches Functions timeout)
          }
        );
        console.log('PDF generation response:', response.data);
        getMonthlyList();
        toast.success(`You've successfully sent the monthly PDF to ${graphPoints?.user_name}`);
        
        // Notify admin that individual PDF was sent to client "x"
        try {
          await axios.post(
            FUNCTIONS_URLS.SendAdminEmailIndividualPDF,
            {
              userName: graphPoints?.user_name,
              userEmail: graphPoints?.email || '',
              month: selectedMonth.format('MMMM YYYY'),
              monthFormatted: selectedMonth.format('YYYY-MM')
            },
            {
              headers: { 'Content-Type': 'application/json' },
              timeout: 30000 // 30 seconds timeout
            }
          );
          toast.info(`Admin notification sent: report sent to ${graphPoints?.user_name}`);
        } catch (adminError) {
          console.error('Failed to notify admin:', adminError);
          toast.warning(`PDF sent to client, but admin notification failed. Check Firebase logs for SendAdminEmailIndividualPDF.`);
        }
      } catch (error) {
        console.error('Error sending PDF:', error);
        console.error('Error response data:', error.response?.data);
        if (error.response) {
          const errorData = error.response.data;
          let errorMessage = errorData?.error || error.response.statusText || 'Unknown error';
          
          // Show detailed error if available
          if (errorData?.details && Array.isArray(errorData.details) && errorData.details.length > 0) {
            const firstError = errorData.details[0];
            if (firstError.error) {
              errorMessage = firstError.error;
              // Add details if available
              if (firstError.details) {
                errorMessage += ` - ${firstError.details}`;
              }
            }
          }
          
          toast.error(errorMessage, {
            autoClose: 10000, // Show for 10 seconds for longer messages
          });
        } else if (error.request) {
          toast.error(`PDF couldn't be sent to ${graphPoints?.user_name}: Network error. Please check your connection.`);
        } else {
          toast.error(`PDF couldn't be sent to ${graphPoints?.user_name}: ${error.message}`);
        }
      }
    }
  }

  return (
    <>
      <Box
        sx={{
          width: "100%",
          paddingTop: { xs: '60px', sm: "0px" }
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FundsPageShell
              title="Monthly Graph Details"
              headerMeta={
                <Button
                  onClick={() => navigate("/admin/add-monthly-graph")}
                  sx={{
                    background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                    color: "#fff",
                    height: "36px",
                    px: 2,
                    fontSize: 13,
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "10px",
                    "&:hover": {
                      background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                      opacity: 0.92,
                      color: "#fff",
                    },
                  }}
                >
                  + Create Monthly Graph
                </Button>
              }
              totalsProps={{
                totals: periodTotals,
                loading: periodTotalsLoading,
                type: "monthlyGraph",
                startDate: periodStartDate,
                endDate: periodEndDate,
              }}
              toolbar={
                <FundsPageToolbar
                  searchValue={searchCustmer}
                  onSearchChange={(event) => setSearchCustmer(event.target.value)}
                  onSearchClear={() => setSearchCustmer("")}
                  searchPlaceholder="Search Customer (Email, Full Name)"
                  periodStartDate={periodStartDate}
                  periodEndDate={periodEndDate}
                  onPeriodStartChange={handlePeriodStartChange}
                  onPeriodEndChange={handlePeriodEndChange}
                  resetKey={datePickerResetKey}
                  filterActionsProps={{
                    clientSegment,
                    onClientSegmentChange: handleClientSegmentChange,
                    onResetFilters: resetAllFilters,
                    filtersActive,
                    showStatusFilter: false,
                  }}
                />
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TableContainer
              rows={monthlyGraphList}
              columns={columns}
              loading={isLoading}
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              handlePageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              paginationShow={true}
            />
          </Grid>
        </Grid>
      </Box>
      {performanceShowModel && (
        <MonthlyCandelChartModel
          open={performanceShowModel}
          onClose={() => setPerformanceShowModel(false)}
          handleOnClose={() => setPerformanceShowModel(false)}
          performanceGraphDetails={performanceGraphDetails}
        />
      )}
    </>
  );
};

export default MonthlyGraph;
